import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Handset, WhatsApp } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Image from 'next/image';
import type { ReactNode } from 'react';

import { TextLink } from '@/components/text-link';
import { joinSgcmMedia } from '@/utils/url/join/public';

import { lebelConfigList } from './config';
import { ICON_SIZE_IN_PIXEL } from './constants';
import styles from './styles.module.scss';
import type { Component, ContactType } from './types';

const contactPrefix: Record<ContactType, string> = {
  hotline: 'tel:',
  whatsApp: 'https://api.whatsapp.com/send?phone=65',
  messenger: '',
  sms: 'sms:',
};

export const ContactItem: Component = ({
  contactType,
  displayMode,
  list,
  iconSize,
  fontSize,
  separator,
  lebelText,
  target,
  verticalAlignment,
  hotlineLayout,
  textClassName,
  onClickHandler,
  onClickMobileHandler,
}) => {
  const { isIpadMini, isDesktop } = useScreenDetect();
  const isIpadMiniMode = isIpadMini();
  const isDesktopMode = isDesktop();
  const hrefPrefix = contactPrefix[contactType as ContactType];
  const labelText = lebelText || lebelConfigList[contactType];
  const singaporeContactNumberPattern = /^[89]\d{7}$/;
  const lastPhoneIndex = list.length - 1;
  const formatPhone = (phone: string) => `${phone.slice(0, 4)} ${phone.slice(4)}`;

  const contactIcons: Record<ContactType, ReactNode> = {
    hotline:
  <Handset
    size={iconSize || ICON_SIZE_IN_PIXEL} />,
    whatsApp:
  <WhatsApp
    className={styles.isGreen}
    size={iconSize || ICON_SIZE_IN_PIXEL} />,
    messenger:
    (
      <Image
        alt="sms"
        height={iconSize || ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconBlueMessenger.svg')}`}
        title="sms"
        width={iconSize || ICON_SIZE_IN_PIXEL} />
    ),
    sms: (
      <Image
        alt="sms"
        height={iconSize || ICON_SIZE_IN_PIXEL}
        src={`${joinSgcmMedia('/public/new-cars/icon/iconSms.svg')}`}
        title="sms"
        width={iconSize || ICON_SIZE_IN_PIXEL} />
    ),
  };

  const icon = contactIcons[contactType as ContactType];

  return (
    <div className={cx(
      styles.containerRoot,
      verticalAlignment === 'top' && styles.isVerticalAlignTop,
      verticalAlignment === 'bottom' && styles.isVerticalAlignBottom, // @suppress longLineCheck
    )}>
      {displayMode !== 'textOnly'
      && (
        <a
          href={`${hrefPrefix}${list[0]}`}
          target={target}>
          <div className={styles.containerIcon}>
            { icon }
          </div>
        </a>
      )}
      {displayMode !== 'iconOnly' && (
        <>
          {contactType === 'hotline' && (
            <div className={cx(
              styles.containerTextPhones,
              hotlineLayout === 'row' && styles.isFlexRow,
              hotlineLayout === 'column' && styles.isFlexColumn,
            )}>
              {
                list.map((item: string, index: number) => (
                  <div
                    key={item}
                    className={styles.textPhones}>
                    {isDesktopMode
                      ? (
                        <div
                          className={cx(
                            styles.textLink,
                            textClassName,
                            fontSize === 'small' && styles.isFontSizeSmall,
                            fontSize === 'extraSmall'
                            && styles.isFontSizeExtraSmall,
                          )}>
                          {formatPhone(item)}
                        </div>
                      )
                      : (
                        <TextLink
                          className={cx(
                            styles.textLink,
                            textClassName,
                            isIpadMiniMode && styles.isDesktopLink,
                            fontSize === 'small' && styles.isFontSizeSmall,
                            fontSize === 'extraSmall'
                            && styles.isFontSizeExtraSmall,
                          )}
                          href={`${hrefPrefix}${item}`}
                          text={formatPhone(item)}
                          onClick={singaporeContactNumberPattern.test(item)
                            ? onClickMobileHandler
                            : onClickHandler} />
                      )}
                    { separator && index < lastPhoneIndex
                    && (
                      <span className={cx(
                        styles.textSeparator,
                        hotlineLayout === 'column' && styles.isHiddenSeparator,
                        fontSize === 'small' && styles.isFontSizeSmall,
                        fontSize === 'extraSmall' && styles.isFontSizeExtraSmall, // @suppress longLineCheck
                      )}>
                        {separator}
                      </span>
                    )}
                  </div>
                ))
              }
            </div>
          )}
          {contactType !== 'hotline' && (
            <TextLink
              className={cx(
                styles.textLink,
                isIpadMiniMode && styles.isDesktopLink,
                fontSize === 'small' && styles.isFontSizeSmall,
                fontSize === 'extraSmall' && styles.isFontSizeExtraSmall, // @suppress longLineCheck
              )}
              href={`${hrefPrefix}${list[0]}`}
              target={target}
              text={labelText || ''}
              onClick={onClickHandler} />
          )}
        </>
      )}
    </div>
  );
};
